import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

function normalizeRow(row) {
  return {
    label: row?.label ?? row?.Parameter ?? row?.parameter ?? '',
    value: row?.value ?? row?.Value ?? '',
  };
}

function normalizeData(technicalData) {
  const headers = technicalData?.headers || ['Parameter', 'Value'];
  const raw = technicalData?.data ?? [];
  const data = raw.length > 0 ? raw.map(normalizeRow) : [{ label: '', value: '' }];
  return { headers, data };
}

/** Single table block: collapsible, default closed, editable rows + add/delete row. */
function EditableTableBlock({
  tableId,
  tableLabel,
  headers,
  data,
  defaultExpanded = false,
  onDataChange,
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const rows = data.length > 0 ? data : [{ label: '', value: '' }];

  const handleCellChange = (rowIndex, field, value) => {
    const newData = rows.map((row, i) =>
      i === rowIndex ? { ...normalizeRow(row), [field]: value } : normalizeRow(row)
    );
    onDataChange?.({ headers, data: newData });
  };

  const handleAddRow = () => {
    onDataChange?.({ headers, data: [...rows.map(normalizeRow), { label: '', value: '' }] });
  };

  const handleDeleteRow = (rowIndex) => {
    if (rows.length <= 1) return;
    const newData = rows.filter((_, i) => i !== rowIndex).map(normalizeRow);
    onDataChange?.({ headers, data: newData });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
          px: 1.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.200' },
        }}
      >
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
          {tableLabel}
        </Typography>
      </Box>
      <Collapse in={open} timeout={200}>
        <Box sx={{ pt: 1 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {(headers || ['Parameter', 'Value']).map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 600, fontSize: 13 }}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 48, fontWeight: 600 }} align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell sx={{ width: '40%', py: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        value={row.label}
                        onChange={(e) => handleCellChange(rowIndex, 'label', e.target.value)}
                        placeholder="Label"
                        sx={{ '& input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '60%', py: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        value={row.value}
                        onChange={(e) => handleCellChange(rowIndex, 'value', e.target.value)}
                        placeholder="Value"
                        sx={{ '& input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 48, py: 0.5 }} align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRow(rowIndex)}
                        disabled={rows.length <= 1}
                        aria-label="Delete row"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{ mt: 1, textTransform: 'none', fontSize: 13 }}
          >
            Add New Row
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

/** One component block: label, quantity, expandable technical specs. */
function ComponentBlock({ component, onComponentChange, onComponentDelete }) {
  const [open, setOpen] = useState(false);
  const hasMultipleTables =
    component.technicalData?.tables &&
    Array.isArray(component.technicalData.tables) &&
    component.technicalData.tables.length > 0;
  const singleTable = hasMultipleTables ? null : normalizeData(component.technicalData);
  const { headers: singleHeaders, data: singleData } = singleTable || {};

  const handleQuantityChange = (qty) => {
    onComponentChange?.({ ...component, quantity: Math.max(1, qty) });
  };

  const handleTechnicalDataChange = (tech) => {
    onComponentChange?.({ ...component, technicalData: tech });
  };

  const handleMultiTableChange = (tableIndex, next) => {
    const tables = [...(component.technicalData?.tables || [])];
    tables[tableIndex] = { ...tables[tableIndex], ...next };
    onComponentChange?.({ ...component, technicalData: { tables } });
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1.5,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
            {component.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            type="number"
            label="Qty"
            value={component.quantity ?? 1}
            onChange={(e) => {
              e.stopPropagation();
              handleQuantityChange(Math.max(1, parseInt(e.target.value, 10) || 1));
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ min: 1, style: { width: 48 } }}
            sx={{ width: 72 }}
          />
          <IconButton
            size="small"
            color="error"
            onClick={(e) => { e.stopPropagation(); onComponentDelete?.(); }}
            aria-label="Remove component"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open} timeout={200}>
        <Box sx={{ p: 1.5, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: 11 }}>
            Technical data (editable)
          </Typography>
          {hasMultipleTables ? (
            component.technicalData.tables.map((tbl, idx) => (
              <EditableTableBlock
                key={tbl.id || idx}
                tableId={tbl.id}
                tableLabel={tbl.label || `Table ${idx + 1}`}
                headers={tbl.headers}
                data={tbl.data || []}
                defaultExpanded={false}
                onDataChange={(next) => handleMultiTableChange(idx, next)}
              />
            ))
          ) : (
            <EditableTableBlock
              tableId="single"
              tableLabel="Specification"
              headers={singleHeaders}
              data={singleData || []}
              defaultExpanded={false}
              onDataChange={handleTechnicalDataChange}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function GemaSystemCard({
  id,
  title,
  components,
  expanded: controlledExpanded,
  onExpandedChange,
  onComponentsChange,
  onDelete,
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const setExpanded = useCallback(
    (value) => {
      if (isControlled) onExpandedChange?.(value);
      else setInternalExpanded(value);
    },
    [isControlled, onExpandedChange]
  );

  const handleComponentChange = useCallback(
    (componentIndex, updatedComponent) => {
      const next = [...(components || [])];
      next[componentIndex] = updatedComponent;
      onComponentsChange?.(next);
    },
    [components, onComponentsChange]
  );

  const handleComponentDelete = useCallback(
    (componentIndex) => {
      const next = (components || []).filter((_, i) => i !== componentIndex);
      onComponentsChange?.(next);
    },
    [components, onComponentsChange]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            sx={{ transition: 'transform 0.2s ease' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, fontSize: '0.9375rem' }}>
            {title || 'Gema Automatic System'}
          </Typography>
        </Box>
        <IconButton size="small" color="error" onClick={onDelete} aria-label="Delete product">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout={200} unmountOnExit>
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontSize: 12 }}>
            Components (each with quantity and technical specs)
          </Typography>
          {(components || []).map((comp, idx) => (
            <ComponentBlock
              key={comp.key || idx}
              component={comp}
              onComponentChange={(updated) => handleComponentChange(idx, updated)}
              onComponentDelete={() => handleComponentDelete(idx)}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
